/**
 * Página de Assinatura — Planos + status + alterar vencimento
 */

import { Check, Crown, CreditCard, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { mockPlans, mockSubscription, mockPaymentMethods } from '@/mocks/data';
import { toast } from 'sonner';

export default function SubscriptionPage() {
  const currentPlan = mockSubscription.plan!;

  return (
    <div>
      <PageHeader title="Assinatura" description="Gerencie seu plano e forma de pagamento" />

      {/* Status atual */}
      <Card className="mb-6 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Crown className="h-5 w-5 text-primary" />
                <h2 className="text-xl font-bold">{currentPlan.name}</h2>
                <StatusBadge status={mockSubscription.status} />
              </div>
              <p className="text-sm text-muted-foreground">{currentPlan.description}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><CreditCard className="h-3 w-3" /> Visa •••• 4242</span>
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Próx. cobrança: {mockSubscription.next_billing_date}</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">R$ {currentPlan.price}</p>
              <p className="text-xs text-muted-foreground">/{currentPlan.billing_cycle}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Planos disponíveis */}
      <h3 className="text-base font-semibold mb-4">Planos Disponíveis</h3>
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3 mb-6">
        {mockPlans.map(plan => {
          const isCurrent = plan.id === currentPlan.id;
          return (
            <Card key={plan.id} className={`relative ${isCurrent ? 'border-primary ring-2 ring-primary/20' : ''}`}>
              {isCurrent && <Badge className="absolute -top-2.5 left-4 bg-primary text-primary-foreground text-[10px]">Plano Atual</Badge>}
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-3xl font-bold">R$ {plan.price}</span>
                  <span className="text-muted-foreground text-sm">/{plan.billing_cycle}</span>
                </div>
                <ul className="space-y-2">
                  {plan.features.map(f => (
                    <li key={f} className="text-sm flex items-center gap-2">
                      <Check className="h-4 w-4 text-success shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button className="w-full" variant={isCurrent ? 'outline' : 'default'} disabled={isCurrent}
                  onClick={() => toast.success(`Upgrade para ${plan.name} solicitado!`)}>
                  {isCurrent ? 'Plano Atual' : 'Selecionar Plano'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Formas de pagamento */}
      <h3 className="text-base font-semibold mb-4">Formas de Pagamento</h3>
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 max-w-2xl">
        {mockPaymentMethods.map(pm => (
          <Card key={pm.id} className={`${pm.is_default ? 'border-primary/30' : ''}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-muted-foreground" />
                <div>
                  {pm.type === 'credit_card' ? (
                    <p className="text-sm font-medium">{pm.card_brand} •••• {pm.card_last4} <span className="text-xs text-muted-foreground">{pm.card_expiry}</span></p>
                  ) : (
                    <p className="text-sm font-medium capitalize">{pm.type}</p>
                  )}
                </div>
              </div>
              {pm.is_default && <Badge variant="outline" className="text-[10px]">Padrão</Badge>}
            </CardContent>
          </Card>
        ))}
        <Button variant="outline" className="h-16 border-dashed" onClick={() => toast.info('Adicionar forma de pagamento (placeholder)')}>
          + Adicionar
        </Button>
      </div>
    </div>
  );
}
